import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { logError } from "@/lib/logger";
import { SUPABASE_URL } from "@/integrations/supabase/client";

export function useDashboardData() {
  const { getAccessToken } = useAuth();
  const fetchingUsers = useRef(false);
  const usersFetched = useRef(false);
  const userNameMap = useRef(new Map<string, string>());
  
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsersWeek: 0,
    newUsersMonth: 0,
    newUsersYear: 0,
    totalContracts: 0,
    totalContractValue: 0,
    userWithMostContracts: { name: "", count: 0 },
    userWithHighestValue: { name: "", value: 0 },
    errorCount: 0,
    emailsSent: 0,
    weeklyUsersData: [],
    totalNewUsers: 0
  });

  const [newUsersTimeline, setNewUsersTimeline] = useState([]);
  const [errorStats, setErrorStats] = useState([]);
  const [emailStats, setEmailStats] = useState([]);
  const [recentErrors, setRecentErrors] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUsers = async () => {
    if (usersFetched.current || fetchingUsers.current) {
      return userNameMap.current;
    }

    fetchingUsers.current = true;
    const accessToken = await getAccessToken();
        
    if (accessToken) {
      try {
        console.log("Fetching users from API...");
        const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-get-users`, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error fetching users: ${response.statusText}`);
        }
        
        const users = await response.json();
        console.log("Users fetched successfully:", users.length);
        
        const newUserNameMap = new Map<string, string>();
        users.forEach((user) => {
          let displayName = user.name;
          
          if (!displayName || displayName.trim() === '') {
            displayName = user.email.split('@')[0];
          }
          
          newUserNameMap.set(user.id, displayName);
        });
        
        userNameMap.current = newUserNameMap;
        
        const activeUsersList = users.slice(0, 5).map((user) => ({
          id: user.id,
          email: user.email,
          lastActive: new Date().toISOString()
        }));
        
        setActiveUsers(activeUsersList);
        usersFetched.current = true;
        
        return newUserNameMap;
      } catch (error) {
        console.error("Error fetching users via edge function:", error);
      } finally {
        fetchingUsers.current = false;
      }
    }
    
    return new Map<string, string>();
  };

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      await fetchUsers();
      
      const { count: totalUsers, error: usersError } = await supabase
        .from('user_roles')
        .select('user_id', { count: 'exact', head: true });

      if (usersError) throw usersError;

      const now = new Date();
      const weekAgo = subDays(now, 7);
      const monthAgo = subDays(now, 30);
      const yearAgo = subDays(now, 365);

      const { data: newUsers, error: newUsersError } = await supabase
        .from('user_roles')
        .select('created_at, user_id')
        .gt('created_at', yearAgo.toISOString());

      if (newUsersError) throw newUsersError;

      const uniqueUserIds = new Set();
      const uniqueUsers = newUsers.filter(user => {
        const duplicate = uniqueUserIds.has(user.user_id);
        uniqueUserIds.add(user.user_id);
        return !duplicate;
      });

      const newUsersWeek = uniqueUsers.filter(user => 
        new Date(user.created_at) >= weekAgo
      ).length;

      const newUsersMonth = uniqueUsers.filter(user => 
        new Date(user.created_at) >= monthAgo
      ).length;

      const newUsersYear = uniqueUsers.length;

      const timelineData = [];
      for (let i = 6; i >= 0; i--) {
        const date = subDays(now, i);
        const dateStr = format(date, 'MMM dd');
        const count = uniqueUsers.filter(user => 
          format(new Date(user.created_at), 'MMM dd') === dateStr
        ).length;
        timelineData.push({ date: dateStr, count });
      }
      setNewUsersTimeline(timelineData);

      const { count: totalContracts, error: contractsError } = await supabase
        .from('contracts')
        .select('*', { count: 'exact', head: true });

      if (contractsError) throw contractsError;

      const { data: contracts, error: contractValuesError } = await supabase
        .from('contracts')
        .select('expected_cost, user_id');

      if (contractValuesError) throw contractValuesError;

      let totalValue = 0;
      const userContractCounts: Record<string, number> = {};
      const userContractValues: Record<string, number> = {};

      contracts.forEach(contract => {
        const cost = parseFloat(String(contract.expected_cost));
        totalValue += cost;
        
        if (userContractCounts[contract.user_id]) {
          userContractCounts[contract.user_id]++;
        } else {
          userContractCounts[contract.user_id] = 1;
        }

        if (userContractValues[contract.user_id]) {
          userContractValues[contract.user_id] += cost;
        } else {
          userContractValues[contract.user_id] = cost;
        }
      });

      let maxContracts = 0;
      let userWithMostContractsId = "";
      Object.entries(userContractCounts).forEach(([userId, count]) => {
        if (count > maxContracts) {
          maxContracts = count;
          userWithMostContractsId = userId;
        }
      });

      let maxValue = 0;
      let userWithHighestValueId = "";
      Object.entries(userContractValues).forEach(([userId, value]) => {
        if (value > maxValue) {
          maxValue = value;
          userWithHighestValueId = userId;
        }
      });

      let userWithMostContractsName = userNameMap.current.get(userWithMostContractsId) || "Unknown";
      let userWithHighestValueName = userNameMap.current.get(userWithHighestValueId) || "Unknown";

      const { data: logsByLevel, error: logsError } = await supabase
        .rpc('count_logs_by_level');

      if (logsError) throw logsError;

      const errorPieData = logsByLevel || [];
      setErrorStats(errorPieData);

      const totalErrors = errorPieData.reduce((sum, item) => sum + Number(item.count), 0);

      const { data: recentErrorsData, error: recentErrorsError } = await supabase
        .from('system_logs')
        .select('id, level, message, location, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      if (recentErrorsError) throw recentErrorsError;
      setRecentErrors(recentErrorsData);

      const { data: emailsByStatus, error: emailsError } = await supabase
        .rpc('count_emails_by_status');

      if (emailsError) throw emailsError;

      const sentEmails = emailsByStatus.find(item => item.status === 'sent');
      const totalSentEmails = sentEmails ? Number(sentEmails.count) : 0;

      setEmailStats(emailsByStatus || []);
      
      // Get weekly user data
      const currentYear = new Date().getFullYear();
      const startOfYear = new Date(currentYear, 0, 1).toISOString();
      const endOfYear = new Date(currentYear, 11, 31).toISOString();
      
      const { data: yearUsers, error: yearUsersError } = await supabase
        .from('user_roles')
        .select('created_at, user_id')
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear)
        .order('created_at', { ascending: true });
      
      if (yearUsersError) throw yearUsersError;
      
      // Initialize weeks array (1-52)
      const weeksData: { week: string, count: number }[] = [];
      for (let i = 1; i <= 52; i++) {
        weeksData.push({ week: `W${i}`, count: 0 });
      }
      
      // Process data to get counts by week
      const uniqueYearlyUserIds = new Set();
      let totalYearlyUsers = 0;
      
      yearUsers.forEach(user => {
        // Skip duplicate user IDs
        if (uniqueYearlyUserIds.has(user.user_id)) return;
        uniqueYearlyUserIds.add(user.user_id);
        totalYearlyUsers++;
        
        // Calculate the week number (1-52)
        const date = new Date(user.created_at);
        const startOfYear = new Date(date.getFullYear(), 0, 1);
        const weekNumber = Math.ceil((((date.getTime() - startOfYear.getTime()) / 86400000) + startOfYear.getDay() + 1) / 7);
        
        // Increment count for this week
        if (weekNumber >= 1 && weekNumber <= 52) {
          weeksData[weekNumber - 1].count++;
        }
      });
      
      // Return only weeks that have data or are before the current week
      const currentDate = new Date();
      const startOfCurrentYear = new Date(currentDate.getFullYear(), 0, 1);
      const currentWeekNumber = Math.ceil((((currentDate.getTime() - startOfCurrentYear.getTime()) / 86400000) + startOfCurrentYear.getDay() + 1) / 7);
      
      const relevantWeeksData = weeksData.slice(0, currentWeekNumber);

      setStats({
        totalUsers,
        newUsersWeek,
        newUsersMonth,
        newUsersYear,
        totalContracts,
        totalContractValue: totalValue,
        userWithMostContracts: { 
          name: userWithMostContractsName, 
          count: maxContracts 
        },
        userWithHighestValue: { 
          name: userWithHighestValueName, 
          value: maxValue 
        },
        errorCount: totalErrors,
        emailsSent: totalSentEmails,
        weeklyUsersData: relevantWeeksData,
        totalNewUsers: totalYearlyUsers
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      logError("Failed to fetch admin dashboard data", "useDashboardData.ts", { error });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();

    const channel = supabase.channel('dashboard-updates')
      
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'user_roles' 
      }, () => {
        console.log("User roles change detected, refreshing dashboard data");
        fetchDashboardData();
      })
      
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'contracts' 
      }, () => {
        console.log("Contracts change detected, refreshing dashboard data");
        fetchDashboardData();
      })
      
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'system_logs' 
      }, () => {
        console.log("New system log detected, refreshing dashboard data");
        fetchDashboardData();
      })
      
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'email_logs' 
      }, () => {
        console.log("Email logs change detected, refreshing dashboard data");
        fetchDashboardData();
      })
      
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'user_invitations' 
      }, () => {
        console.log("User invitations change detected, refreshing dashboard data");
        fetchDashboardData();
      })
      
      .subscribe();

    return () => {
      console.log("Cleaning up real-time subscription");
      supabase.removeChannel(channel);
    };
  }, [fetchDashboardData]);

  return {
    stats,
    newUsersTimeline,
    errorStats,
    emailStats,
    recentErrors,
    activeUsers,
    isLoading,
    fetchDashboardData
  };
}
